import { useState, useEffect, useMemo } from 'react';
import { useCustom, useCustomMutation, useApiUrl } from '@refinedev/core';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { ColorPicker } from '@/components/ui/color-picker';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  FormField,
  MultiCurrencyPrices,
  type MultiCurrencyPricesValue,
} from '@/components/forms';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from '@/components/ui/sheet';
import {
  LanguageSwitcher,
  TranslationsForm,
  createEmptyTranslationData,
  type TranslationData,
  type TranslationFieldConfig,
  type TranslationLanguage,
  type LanguageCode,
} from '@/components/translations';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DateTimePicker } from '@/components/ui/date-time-picker';
import { Separator } from '@/components/ui/separator';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import {
  Loader2, Plus, Pencil, Trash2, Tag, Palette, Users,
  BookOpen, Radio, Lock, Hash, Copy,
  ChevronDown, ChevronRight, GripVertical, CreditCard,
} from 'lucide-react';
import { MultiSelect } from '@/components/ui/multi-select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { SortableList, DragHandle } from '@/components/dnd';
import { notify } from '@/lib/notify';
import { resolveApiUrl } from '@/providers/api-url';
import { PriceCurrencyTooltip } from '@/components/shared/PriceCurrencyTooltip';
import { ContentTagsField } from '@/components/shared';
import { useAffectedSubscribersCheck } from '@/hooks/use-affected-subscribers-check';
import type { PricingTier, BonusTemplate, PricingTierEntitlement as TierEntitlement } from '@/resources/channel-passes/types';
import {
  DEFAULT_TIER_CARD_APPEARANCE,
  type PricingTierCardAppearance,
  type PricingTierCardThemeColors,
} from './types';
import { MembershipCardPreview } from './MembershipCardPreview';

function isCardThemeColors(value: unknown): value is PricingTierCardThemeColors {
  if (typeof value !== 'object' || value === null) { return false; }
  if (!('bg' in value) || typeof value.bg !== 'string') { return false; }
  if (!('patternPrimary' in value) || typeof value.patternPrimary !== 'string') { return false; }
  if (!('patternSecondary' in value) || typeof value.patternSecondary !== 'string') { return false; }
  return true;
}

function isCardAppearance(value: unknown): value is PricingTierCardAppearance {
  if (typeof value !== 'object' || value === null) { return false; }
  if (!('light' in value) || !isCardThemeColors(value.light)) { return false; }
  if (!('dark' in value) || !isCardThemeColors(value.dark)) { return false; }
  return true;
}

const API_URL = resolveApiUrl();

interface MutationResponseWithId {
  data?: {
    data?: { id?: number };
  };
}

function extractCreatedId(response: unknown): number | null {
  if (typeof response !== 'object' || response === null) {return null;}
  const r = response as MutationResponseWithId;
  return r.data?.data?.id ?? null;
}

const TIER_TRANSLATION_FIELDS: TranslationFieldConfig[] = [
  {
    name: 'name',
    label: 'Название тарифа',
    type: 'input',
  },
];

const BONUS_TRANSLATION_FIELDS: TranslationFieldConfig[] = [
  {
    name: 'name',
    label: 'Название привилегии',
    type: 'input',
    required: true,
  },
  {
    name: 'description',
    label: 'Описание',
    type: 'textarea',
  },
];

const BONUS_TYPES = [
  { value: 'NUMBER', label: 'Число' },
  { value: 'BOOLEAN', label: 'Да/Нет' },
  { value: 'ARRAY', label: 'Массив' },
  { value: 'STRING', label: 'Строка' },
] as const;

const DURATION_MODES = [
  { value: 'TIER', label: 'Как у тарифа', hint: 'Привилегия действует столько же, сколько тариф' },
  { value: 'CUSTOM', label: 'Свой срок', hint: 'Укажите свой срок действия в днях' },
  { value: 'ABSOLUTE', label: 'До даты', hint: 'Доступ действует до указанной даты' },
  { value: 'PERMANENT', label: 'Навсегда', hint: 'Привилегия остаётся навсегда после активации' },
] as const;

const CURRENCY_SYMBOLS: Record<string, string> = {
  UZS: 'сум',
  USD: '$',
  RUB: '₽',
  EUR: '€',
  KZT: '₸',
  KGS: 'сом',
  TJS: 'смн',
};

const GRADIENT_PRESETS = [
  { name: 'Золотой', from: '#FCA311', to: '#FFD99A', angle: 264 },
  { name: 'Синий', from: '#375086', to: '#A1BFFF', angle: 183 },
  { name: 'Зелёный', from: '#2D6A4F', to: '#95D5B2', angle: 180 },
  { name: 'Фиолетовый', from: '#5A189A', to: '#C77DFF', angle: 180 },
  { name: 'Красный', from: '#9D0208', to: '#F48C06', angle: 180 },
] as const;

/** Типы сущностей для доступов */
const ACCESS_TARGET_TYPES = [
  { value: 'COURSE', label: 'Курс', icon: BookOpen, resource: 'courses' },
  { value: 'CHANNEL', label: 'Канал', icon: Radio, resource: 'telegram-channels' },
  { value: 'BOOK', label: 'Книга', icon: BookOpen, resource: 'books' },
] as const;

/** Типы курсов для фильтрации при quantity-режиме */
const COURSE_TYPES = [
  { value: '', label: 'Все типы' },
  { value: 'ONLINE', label: 'Онлайн' },
  { value: 'MEETING', label: 'Клуб' },
] as const;

type AccessTargetTypeValue = (typeof ACCESS_TARGET_TYPES)[number]['value'];

/** Режим выбора: конкретный объект или количество по выбору */
type AccessSelectionMode = 'specific' | 'quantity';

/** Данные доступа, хранимые в initialValue бонуса */
interface AccessBonusValue {
  accessTargetType: AccessTargetTypeValue;
  selectionMode?: AccessSelectionMode; // undefined = 'specific' (обратная совместимость)
  targetId: number; // 0 при quantity
  targetName: string;
  quantity?: number; // кол-во объектов по выбору (при selectionMode='quantity')
  courseType?: string; // фильтр по типу курса (при quantity + COURSE)
}

/** Маппинг API durationMode -> UI durationMode */
function apiToUiDurationMode(apiMode: string): BonusTemplate['durationMode'] {
  switch (apiMode) {
    case 'RELATIVE': return 'CUSTOM';
    case 'ABSOLUTE': return 'ABSOLUTE';
    case 'INHERIT':
    default: {
      // INHERIT без durationDays = TIER, с PERMANENT логикой = PERMANENT
      // Но мы не можем отличить TIER от PERMANENT по durationMode,
      // поэтому оставляем как 'TIER' — PERMANENT определяется по контексту
      return 'TIER';
    }
  }
}

function isAccessBonus(bonus: BonusTemplate): boolean {
  if (bonus.type !== 'STRING') {
    return false;
  }
  const val = bonus.initialValue;
  if (typeof val === 'object' && val !== null && 'accessTargetType' in val) {
    return true;
  }
  if (typeof val === 'string') {
    try {
      const parsed: unknown = JSON.parse(val);
      return typeof parsed === 'object' && parsed !== null && 'accessTargetType' in parsed;
    } catch {
      return false;
    }
  }
  return false;
}

function isAccessBonusValue(val: unknown): val is AccessBonusValue {
  return (
    typeof val === 'object' &&
    val !== null &&
    'accessTargetType' in val &&
    'targetId' in val &&
    'targetName' in val
  );
}

function parseAccessValue(bonus: BonusTemplate): AccessBonusValue | null {
  const val = bonus.initialValue;
  if (isAccessBonusValue(val)) {
    return val;
  }
  if (typeof val === 'string') {
    try {
      const parsed: unknown = JSON.parse(val);
      if (isAccessBonusValue(parsed)) {
        return parsed;
      }
    } catch {
      // не JSON
    }
  }
  return null;
}

function getSelectionMode(val: AccessBonusValue): AccessSelectionMode {
  return val.selectionMode ?? 'specific';
}

/**
 * Конвертирует PricingTierEntitlement в shape BonusTemplate с access-семантикой,
 * чтобы существующий UI отрендерил его как «доступ» без переписывания.
 * id записи в BonusTemplate-shape = -entitlement.id (отрицательный) для отличия.
 */
function entitlementToBonusTemplate(e: TierEntitlement): BonusTemplate {
  const access: AccessBonusValue = e.selectionMode === 'specific'
    ? {
        accessTargetType: e.targetType,
        selectionMode: 'specific',
        targetId: e.targetId ?? 0,
        targetName: `Доступ #${e.targetId ?? '?'}`,
      }
    : {
        accessTargetType: e.targetType,
        selectionMode: 'quantity',
        targetId: 0,
        targetName: '',
        quantity: e.quantity ?? 0,
        ...(e.filterCourseType ? { courseType: e.filterCourseType } : {}),
      };
  return {
    id: -e.id,
    tierId: e.pricingTierId,
    type: 'STRING',
    initialValue: JSON.stringify(access),
    durationMode: e.accessDurationDays ? 'CUSTOM' : 'TIER',
    customDurationDays: e.accessDurationDays,
    expiresAt: null,
    order: e.order,
    translations: {},
    createdAt: e.createdAt,
  };
}

function isQuantityAccess(val: AccessBonusValue): boolean {
  return getSelectionMode(val) === 'quantity';
}

function getAccessIcon(targetType: string) {
  const found = ACCESS_TARGET_TYPES.find((t) => t.value === targetType);
  return found?.icon ?? Lock;
}

function getAccessLabel(targetType: string) {
  return ACCESS_TARGET_TYPES.find((t) => t.value === targetType)?.label ?? targetType;
}

function getTierName(tier: PricingTier, lang: string): string {
  return tier.translations?.[lang]?.name
    ?? tier.translations?.ru?.name
    ?? tier.translations?.en?.name
    ?? `Тариф #${tier.id}`;
}

function _formatPrice(amount: number, currency: string): string {
  const symbol = CURRENCY_SYMBOLS[currency] ?? currency;
  return `${new Intl.NumberFormat('ru-RU').format(amount)} ${symbol}`;
}

/** Product key — передаётся для привязки тарифа к конкретному продукту */
export interface ProductKey {
  channelPassId?: number;
  courseId?: number;
  subscriptionPlanId?: number;
  bookId?: number;
}

interface PricingTiersSectionProps {
  productKey: ProductKey;
  languages?: TranslationLanguage[];
}

export function PricingTiersSection({ productKey, languages }: PricingTiersSectionProps) {
  const queryClient = useQueryClient();

  const queryParams: Record<string, number> = {};
  if (productKey.channelPassId) {
    queryParams.channelPassId = productKey.channelPassId;
  }
  if (productKey.courseId) {
    queryParams.courseId = productKey.courseId;
  }
  if (productKey.subscriptionPlanId) {
    queryParams.subscriptionPlanId = productKey.subscriptionPlanId;
  }
  if (productKey.bookId) {
    queryParams.bookId = productKey.bookId;
  }

  const { query: tiersQueryResult } = useCustom<PricingTier[]>({
    url: `${API_URL}/pricing-tiers/admin`,
    method: 'get',
    config: {
      query: queryParams,
    },
  });

  const tiers: PricingTier[] = useMemo(
    () => tiersQueryResult?.data?.data ?? [],
    [tiersQueryResult?.data?.data]
  );
  const isLoadingTiers = tiersQueryResult?.isLoading ?? false;

  const [tierModalOpen, setTierModalOpen] = useState(false);
  const [editingTier, setEditingTier] = useState<PricingTier | null>(null);
  const [duplicateSource, setDuplicateSource] = useState<PricingTier | null>(null);
  const [deleteTier, setDeleteTier] = useState<PricingTier | null>(null);
  const [expandedTiers, setExpandedTiers] = useState<Set<number>>(new Set());

  // Bonus management state (per-tier, inline)
  const [bonusModalOpen, setBonusModalOpen] = useState(false);
  const [editingBonusForTier, setEditingBonusForTier] = useState<PricingTier | null>(null);
  const [editingBonus, setEditingBonus] = useState<BonusTemplate | null>(null);
  const [accessModalOpen, setAccessModalOpen] = useState(false);
  const [editingAccessBonus, setEditingAccessBonus] = useState<BonusTemplate | null>(null);

  const [currentLang] = useState<string>('ru');

  const deleteMutation = useCustomMutation();
  const reorderMutation = useCustomMutation();
  const bonusSaveMutation = useCustomMutation();
  const bonusDeleteMutation = useCustomMutation();

  const { checkAndProceed, dialogElement: affectedSubscribersDialog } = useAffectedSubscribersCheck();

  const refetchTiers = () => {
    queryClient.invalidateQueries({ queryKey: ['data'] });
  };

  const toggleExpanded = (tierId: number) => {
    setExpandedTiers((prev) => {
      const next = new Set(prev);
      if (next.has(tierId)) {
        next.delete(tierId);
      } else {
        next.add(tierId);
      }
      return next;
    });
  };

  const handleOpenCreate = () => {
    setEditingTier(null);
    setDuplicateSource(null);
    setTierModalOpen(true);
  };

  const handleOpenEdit = (tier: PricingTier) => {
    setEditingTier(tier);
    setDuplicateSource(null);
    setTierModalOpen(true);
  };

  // Открыть Sheet редактирования тарифа автоматически, если в URL ?tier=X
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const tierIdRaw = params.get('tier');
    if (!tierIdRaw) return;
    const tierId = Number(tierIdRaw);
    if (!Number.isFinite(tierId) || tiers.length === 0) return;
    const target = tiers.find((t) => t.id === tierId);
    if (!target) return;
    setEditingTier(target);
    setDuplicateSource(null);
    setTierModalOpen(true);
    params.delete('tier');
    const next = params.toString();
    const url = `${window.location.pathname}${next ? `?${next}` : ''}${window.location.hash}`;
    window.history.replaceState(null, '', url);
  }, [tiers]);

  const handleDuplicateTier = (tier: PricingTier) => {
    setEditingTier(null);
    setDuplicateSource(tier);
    setTierModalOpen(true);
  };

  // Bonus handlers (inline)
  const handleAddBonus = (tier: PricingTier) => {
    setEditingBonusForTier(tier);
    setEditingBonus(null);
    setBonusModalOpen(true);
  };

  const handleEditBonus = (tier: PricingTier, bonus: BonusTemplate) => {
    setEditingBonusForTier(tier);
    setEditingBonus(bonus);
    setBonusModalOpen(true);
  };

  const handleAddAccess = (tier: PricingTier) => {
    setEditingBonusForTier(tier);
    setEditingAccessBonus(null);
    setAccessModalOpen(true);
  };

  const handleEditAccess = (tier: PricingTier, bonus: BonusTemplate) => {
    setEditingBonusForTier(tier);
    setEditingAccessBonus(bonus);
    setAccessModalOpen(true);
  };

  const handleDeleteBonus = (tier: PricingTier, bonus: BonusTemplate) => {
    const bonusName = bonus.translations?.ru?.name ?? bonus.translations?.en?.name ?? `Привилегия #${bonus.id}`;

    // bonus.id < 0 → это entitlement (entitlement.id = -bonus.id), не BonusTemplate.
    const isEntitlement = bonus.id < 0;
    const realId = isEntitlement ? -bonus.id : bonus.id;

    // Для entitlement: сначала revoke у существующих (если запрошено) ДО delete,
    // потому что CASCADE на удалении уничтожит данные.
    const deleteEntitlementUrl = `${API_URL}/pricing-tiers/admin/${tier.id}/entitlements/${realId}`;
    const deleteBonusUrl = `${API_URL}/pricing-tiers/admin/${tier.id}/bonuses/${realId}`;

    checkAndProceed({
      tierId: tier.id,
      changeDescription: `Удаление ${isEntitlement ? 'доступа' : 'привилегии'} "${bonusName}"`,
      operationType: 'remove',
      onProceed: () => new Promise<void>((resolve, reject) => {
        bonusDeleteMutation.mutate(
          {
            url: isEntitlement ? deleteEntitlementUrl : deleteBonusUrl,
            method: 'delete',
            values: {},
          },
          {
            onSuccess: () => {
              notify.success(isEntitlement ? 'Доступ удалён' : 'Привилегия удалена');
              refetchTiers();
              resolve();
            },
            onError: () => {
              notify.error('Ошибка при удалении');
              reject();
            },
          },
        );
      }),
      onApplyToExisting: () => new Promise<void>((resolve, reject) => {
        if (isEntitlement) {
          // Для entitlement: revoke у существующих ДО delete (CASCADE уничтожит данные).
          bonusSaveMutation.mutate(
            {
              url: `${API_URL}/pricing-tiers/admin/${tier.id}/entitlements/${realId}/revoke`,
              method: 'post',
              values: {},
            },
            {
              onSuccess: () => {
                notify.success('Доступ отозван у существующих подписчиков');
                resolve();
              },
              onError: () => {
                notify.error('Ошибка при отзыве доступа');
                reject();
              },
            },
          );
          return;
        }
        bonusSaveMutation.mutate(
          {
            url: `${API_URL}/pricing-tiers/admin/${tier.id}/bonuses/${realId}/revoke`,
            method: 'post',
            values: {},
          },
          {
            onSuccess: () => {
              notify.success('Привилегия отозвана у существующих подписчиков');
              resolve();
            },
            onError: () => {
              notify.error('Ошибка при отзыве привилегии');
              reject();
            },
          },
        );
      }),
    });
  };

  const handleSaveAccess = (accessData: AccessBonusSaveData) => {
    if (!editingBonusForTier) {
      return;
    }

    if (accessData.durationMode === 'CUSTOM' && (!accessData.customDurationDays || accessData.customDurationDays <= 0)) {
      notify.error('Укажите количество дней для режима "Свой срок"');
      return;
    }

    // Маппинг в PricingTierEntitlement-shape
    const accessDurationDays = accessData.durationMode === 'CUSTOM'
      ? accessData.customDurationDays
      : null;

    const entitlementValues: Record<string, unknown> = {
      targetType: accessData.accessTargetType,
      selectionMode: accessData.selectionMode,
      ...(accessData.selectionMode === 'specific'
        ? { targetId: accessData.targetId }
        : { quantity: accessData.quantity ?? 0 }),
      ...(accessData.selectionMode === 'quantity' && accessData.accessTargetType === 'COURSE' && accessData.courseType
        ? { filterCourseType: accessData.courseType }
        : {}),
      accessDurationDays,
      isActive: true,
    };

    const tierId = editingBonusForTier.id;
    // editingAccessBonus.id у нас отрицательный (см. entitlementToBonusTemplate);
    // реальный entitlement id = -editingAccessBonus.id.
    const entitlementId = editingAccessBonus ? -editingAccessBonus.id : null;

    if (entitlementId !== null) {
      let applyDecision = false;
      checkAndProceed({
        tierId,
        changeDescription: `Обновление доступа "${accessData.targetName}"`,
        operationType: 'update',
        onApplyToExisting: () => {
          applyDecision = true;
          return Promise.resolve();
        },
        onProceed: () => new Promise<void>((resolve, reject) => {
          bonusSaveMutation.mutate(
            {
              url: `${API_URL}/pricing-tiers/admin/${tierId}/entitlements/${entitlementId}`,
              method: 'put',
              values: { ...entitlementValues, applyToExisting: applyDecision },
            },
            {
              onSuccess: () => {
                setAccessModalOpen(false);
                notify.success(
                  applyDecision
                    ? 'Доступ обновлён (применено к существующим подпискам)'
                    : 'Доступ обновлён',
                );
                refetchTiers();
                resolve();
              },
              onError: () => {
                notify.error('Ошибка при обновлении доступа');
                reject();
              },
            },
          );
        }),
      });
    } else {
      // ADD: после POST получаем id и вызываем /apply при выборе пользователя.
      let createdEntitlementId: number | null = null;
      checkAndProceed({
        tierId,
        changeDescription: `Добавление доступа "${accessData.targetName}"`,
        operationType: 'add',
        onProceed: () => new Promise<void>((resolve, reject) => {
          bonusSaveMutation.mutate(
            {
              url: `${API_URL}/pricing-tiers/admin/${tierId}/entitlements`,
              method: 'post',
              values: entitlementValues,
            },
            {
              onSuccess: (response) => {
                createdEntitlementId = extractCreatedId(response);
                setAccessModalOpen(false);
                notify.success('Доступ добавлен');
                refetchTiers();
                resolve();
              },
              onError: () => {
                notify.error('Ошибка при создании доступа');
                reject();
              },
            },
          );
        }),
        onApplyToExisting: () => new Promise<void>((resolve, reject) => {
          if (!createdEntitlementId) {
            notify.error('Не удалось определить ID созданного доступа');
            reject();
            return;
          }
          bonusSaveMutation.mutate(
            {
              url: `${API_URL}/pricing-tiers/admin/${tierId}/entitlements/${createdEntitlementId}/apply`,
              method: 'post',
              values: {},
            },
            {
              onSuccess: () => {
                notify.success('Доступ применён к существующим подписчикам');
                resolve();
              },
              onError: () => {
                notify.error('Ошибка при применении доступа');
                reject();
              },
            },
          );
        }),
      });
    }
  };

  const handleSaveBonus = (bonusData: {
    type: BonusTemplate['type'];
    initialValue: string;
    durationMode: BonusTemplate['durationMode'];
    customDurationDays: number | null;
    order: number;
    translations: Record<string, { name: string; description?: string }>;
  }) => {
    if (!editingBonusForTier) {
      return;
    }

    if (bonusData.durationMode === 'CUSTOM' && (!bonusData.customDurationDays || bonusData.customDurationDays <= 0)) {
      notify.error('Укажите количество дней для режима "Свой срок"');
      return;
    }

    let parsedValue: unknown = bonusData.initialValue;
    if (bonusData.type === 'NUMBER') {
      parsedValue = Number(bonusData.initialValue) || 0;
    } else if (bonusData.type === 'BOOLEAN') {
      parsedValue = bonusData.initialValue === 'true';
    } else if (bonusData.type === 'ARRAY') {
      try {
        parsedValue = JSON.parse(bonusData.initialValue);
      } catch {
        parsedValue = [];
      }
    }

    const DURATION_MODE_MAP: Record<string, string> = {
      TIER: 'INHERIT',
      CUSTOM: 'RELATIVE',
      PERMANENT: 'INHERIT',
    };

    const translationsArray = Object.entries(bonusData.translations)
      .filter(([, t]) => t.name?.trim())
      .map(([langCode, t]) => ({
        languageCode: langCode,
        name: t.name,
        description: t.description ?? undefined,
      }));

    const values = {
      type: bonusData.type,
      initialValue: parsedValue,
      durationMode: DURATION_MODE_MAP[bonusData.durationMode] ?? bonusData.durationMode,
      durationDays: bonusData.durationMode === 'CUSTOM' ? bonusData.customDurationDays : null,
      order: bonusData.order,
      translations: translationsArray,
    };

    const bonusName = bonusData.translations.ru?.name ?? 'Привилегия';
    const tierId = editingBonusForTier.id;

    if (editingBonus) {
      let applyDecision = false;
      checkAndProceed({
        tierId,
        changeDescription: `Обновление привилегии "${bonusName}"`,
        operationType: 'update',
        onApplyToExisting: () => {
          applyDecision = true;
          return Promise.resolve();
        },
        onProceed: () => new Promise<void>((resolve, reject) => {
          bonusSaveMutation.mutate(
            {
              url: `${API_URL}/pricing-tiers/admin/${tierId}/bonuses/${editingBonus.id}`,
              method: 'put',
              values: { ...values, applyToExisting: applyDecision },
            },
            {
              onSuccess: () => {
                setBonusModalOpen(false);
                notify.success(
                  applyDecision
                    ? 'Привилегия обновлена (применено к существующим подпискам)'
                    : 'Привилегия обновлена',
                );
                refetchTiers();
                resolve();
              },
              onError: () => {
                notify.error('Ошибка при обновлении привилегии');
                reject();
              },
            },
          );
        }),
      });
    } else {
      let createdBonusId: number | null = null;
      checkAndProceed({
        tierId,
        changeDescription: `Добавление привилегии "${bonusName}"`,
        operationType: 'add',
        onProceed: () => new Promise<void>((resolve, reject) => {
          bonusSaveMutation.mutate(
            {
              url: `${API_URL}/pricing-tiers/admin/${tierId}/bonuses`,
              method: 'post',
              values,
            },
            {
              onSuccess: (response) => {
                createdBonusId = extractCreatedId(response);
                setBonusModalOpen(false);
                notify.success('Привилегия создана');
                refetchTiers();
                resolve();
              },
              onError: () => {
                notify.error('Ошибка при создании привилегии');
                reject();
              },
            },
          );
        }),
        onApplyToExisting: () => new Promise<void>((resolve, reject) => {
          if (!createdBonusId) {
            notify.error('Не удалось определить ID созданной привилегии');
            reject();
            return;
          }
          bonusSaveMutation.mutate(
            {
              url: `${API_URL}/pricing-tiers/admin/${tierId}/bonuses/${createdBonusId}/apply`,
              method: 'post',
              values: {},
            },
            {
              onSuccess: () => {
                notify.success('Привилегия применена к существующим подписчикам');
                resolve();
              },
              onError: () => {
                notify.error('Ошибка при применении привилегии к подписчикам');
                reject();
              },
            },
          );
        }),
      });
    }
  };

  const handleReorderBonuses = (tier: PricingTier, reordered: BonusTemplate[]) => {
    for (let idx = 0; idx < reordered.length; idx++) {
      const b = reordered[idx];
      if (b.order !== idx) {
        bonusSaveMutation.mutate({
          url: `${API_URL}/pricing-tiers/admin/${tier.id}/bonuses/${b.id}`,
          method: 'put',
          values: { order: idx },
        });
      }
    }
    refetchTiers();
  };

  const handleDeleteTier = () => {
    if (!deleteTier) {
      return;
    }
    deleteMutation.mutate(
      {
        url: `${API_URL}/pricing-tiers/admin/${deleteTier.id}`,
        method: 'delete',
        values: {},
      },
      {
        onSuccess: () => {
          notify.success('Тариф удалён');
          setDeleteTier(null);
          refetchTiers();
        },
        onError: (error: { message?: string }) => {
          notify.error(error.message ?? 'Ошибка при удалении тарифа');
        },
      },
    );
  };

  const handleTierSaved = () => {
    setTierModalOpen(false);
    setEditingTier(null);
    setDuplicateSource(null);
    refetchTiers();
  };

  return (
    <>
      <div>
        <div className="flex items-center justify-end gap-4 mb-4">
          <Button size="sm" className="shrink-0" onClick={handleOpenCreate}>
            <Plus className="mr-1 h-4 w-4" />
            Добавить тариф
          </Button>
        </div>

        {/* Stats summary — like modules */}
        {tiers.length > 0 && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4 p-3 bg-muted/30 rounded-lg">
            <span>Всего:</span>
            <span className="font-medium text-foreground">
              {tiers.length} {tiers.length === 1 ? 'тариф' : (tiers.length < 5 ? 'тарифа' : 'тарифов')}
            </span>
            <span>&middot;</span>
            <span className="font-medium text-foreground">
              {tiers.reduce((acc, t) => acc + (t.bonusTemplates?.length ?? 0), 0)} привилегий
            </span>
          </div>
        )}

        {/* Tiers List */}
        {isLoadingTiers && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="text-muted-foreground h-5 w-5 animate-spin" />
          </div>
        )}
        {!isLoadingTiers && tiers.length === 0 && (
          <div className="text-center text-muted-foreground py-12 border rounded-lg bg-muted/20">
            <p>Нет тарифов. Добавьте первый тариф.</p>
          </div>
        )}
        {!isLoadingTiers && tiers.length > 0 && (
          <div className="space-y-2">
            <SortableList
              items={tiers.map((t) => ({ ...t, id: t.id }))}
              onReorder={(reordered) => {
                const updates = reordered
                  .map((item, index) => ({ id: item.id, order: index }))
                  .filter((u) => {
                    const original = tiers.find((t) => t.id === u.id);
                    return original && original.order !== u.order;
                  });

                if (updates.length === 0) {
                  return;
                }

                for (const u of updates) {
                  reorderMutation.mutate({
                    url: `${API_URL}/pricing-tiers/admin/${u.id}`,
                    method: 'put',
                    values: { order: u.order },
                  });
                }

                refetchTiers();
              }}
              renderItem={({ item, dragHandleProps }) => {
                const tier = item as PricingTier;
                const isExpanded = expandedTiers.has(tier.id);
                const tierName = getTierName(tier, currentLang);
                const bonusCount = tier.bonusTemplates?.length ?? 0;
                return (
                  <div className="border rounded-lg">
                    {/* Tier Header — like module header */}
                    <div className="flex items-center gap-2 p-3 bg-muted/50">
                      <div {...dragHandleProps}>
                        <GripVertical className="h-4 w-4 text-muted-foreground cursor-grab" />
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="p-0 h-auto"
                        onClick={() => toggleExpanded(tier.id)}
                      >
                        {isExpanded ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                      </Button>
                      {tier.gradientFrom && tier.gradientTo && (
                        <div
                          className="h-4 w-4 rounded-full shrink-0 border"
                          style={{
                            background: `linear-gradient(${tier.gradientAngle ?? 180}deg, ${tier.gradientFrom}, ${tier.gradientTo})`,
                          }}
                        />
                      )}
                      <span className="font-medium flex-1">{tierName}</span>
                      <Badge variant={tier.isActive ? 'default' : 'secondary'} className="shrink-0">
                        {tier.isActive ? 'Активен' : 'Неактивен'}
                      </Badge>
                      {(() => {
                        const isPromoActive = !!tier.promoPrices && !!tier.promoUntil && new Date(tier.promoUntil) > new Date();
                        const baseFree = (tier.prices?.[tier.baseCurrency] ?? 0) === 0;
                        const promoFree = isPromoActive && (tier.promoPrices?.[tier.baseCurrency] ?? 0) === 0;
                        return (
                          <>
                            {baseFree ? (
                              <Badge variant="outline" className="shrink-0 text-xs border-green-500 text-green-600">
                                Бесплатно
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="shrink-0 text-xs">
                                <PriceCurrencyTooltip
                                  prices={tier.prices}
                                  baseCurrency={tier.baseCurrency}
                                  className={isPromoActive ? 'text-xs line-through text-muted-foreground' : 'text-xs'}
                                />
                              </Badge>
                            )}
                            {isPromoActive && !baseFree && (
                              <Badge variant="outline" className="shrink-0 text-xs border-green-500 text-green-600">
                                {promoFree ? (
                                  'Бесплатно'
                                ) : (
                                  <PriceCurrencyTooltip
                                    prices={tier.promoPrices ?? {}}
                                    baseCurrency={tier.baseCurrency}
                                    className="text-xs"
                                  />
                                )}
                              </Badge>
                            )}
                          </>
                        );
                      })()}
                      {bonusCount > 0 && (
                        <Badge variant="secondary" className="shrink-0">
                          {bonusCount} {bonusCount === 1 ? 'привилегия' : (bonusCount < 5 ? 'привилегии' : 'привилегий')}
                        </Badge>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handleOpenEdit(tier)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        title="Дублировать для другого языка"
                        onClick={() => handleDuplicateTier(tier)}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => setDeleteTier(tier)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>

                    {/* Expanded content — bonuses */}
                    {isExpanded && (
                      <div className="p-3 pt-0">
                        <div className="mt-2">
                          <BonusTabsSection
                            bonuses={tier.bonusTemplates ?? []}
                            tier={tier}
                            onAddAccess={() => handleAddAccess(tier)}
                            onEditAccess={(bonus) => handleEditAccess(tier, bonus)}
                            onDeleteBonus={(bonus) => handleDeleteBonus(tier, bonus)}
                            onAddBonus={() => handleAddBonus(tier)}
                            onEditBonus={(bonus) => handleEditBonus(tier, bonus)}
                            onReorderBonuses={(reordered) => handleReorderBonuses(tier, reordered)}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                );
              }}
            />
          </div>
          )}
      </div>

      <TierModal
        open={tierModalOpen}
        onOpenChange={setTierModalOpen}
        productKey={productKey}
        tier={editingTier}
        prefill={duplicateSource}
        onSaved={handleTierSaved}
        languages={languages}
        onCheckAffected={editingTier ? (params) => {
          checkAndProceed({ ...params, tierId: editingTier.id });
        } : undefined}
      />

      <BonusModal
        open={bonusModalOpen}
        onOpenChange={setBonusModalOpen}
        bonus={editingBonus}
        onSave={handleSaveBonus}
        isSaving={bonusSaveMutation.mutation.isPending}
        languages={languages}
      />

      <AccessBonusModal
        open={accessModalOpen}
        onOpenChange={setAccessModalOpen}
        bonus={editingAccessBonus}
        onSave={handleSaveAccess}
        isSaving={bonusSaveMutation.mutation.isPending}
      />

      <AlertDialog open={!!deleteTier} onOpenChange={() => setDeleteTier(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Удалить тариф?</AlertDialogTitle>
            <AlertDialogDescription>
              Это действие нельзя отменить. Тариф будет удалён безвозвратно.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteTier}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Удалить
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {affectedSubscribersDialog}
    </>
  );
}

interface TierModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  productKey: ProductKey;
  tier: PricingTier | null;
  /** Префилл для режима создания (например, при дублировании). Игнорируется если tier !== null. */
  prefill?: PricingTier | null;
  onSaved: () => void;
  languages?: TranslationLanguage[];
  onCheckAffected?: (params: {
    changeDescription: string;
    operationType: 'add' | 'remove' | 'update';
    onProceed: () => Promise<void>;
    onApplyToExisting?: () => Promise<void>;
  }) => void;
}

function TierModal({ open, onOpenChange, productKey, tier, prefill, onSaved, languages, onCheckAffected }: TierModalProps) {
  const isEditTier = !!tier;
  // При дублировании используем prefill как источник начальных значений
  const source = tier ?? prefill ?? null;

  const [durationDays, setDurationDays] = useState('');
  const [pricing, setPricing] = useState<MultiCurrencyPricesValue>({
    price: null, currency: 'UZS', prices: null, baseCurrency: 'UZS',
  });
  const [tierIsActive, setTierIsActive] = useState(true);
  const [tierTranslations, setTierTranslations] = useState<TranslationData>(createEmptyTranslationData);
  const [tierActiveLanguage, setTierActiveLanguage] = useState<LanguageCode>('ru');
  const [imageFile, setImageFile] = useState<{ id: number; url: string } | null>(null);
  const [gradientFrom, setGradientFrom] = useState('');
  const [gradientTo, setGradientTo] = useState('');
  const [gradientAngle, setGradientAngle] = useState('180');
  const [tierIsFamily, setTierIsFamily] = useState(false);
  const [tierFamilySlots, setTierFamilySlots] = useState('0');
  const [promoPricing, setPromoPricing] = useState<MultiCurrencyPricesValue>({
    price: null, currency: 'UZS', prices: null, baseCurrency: 'UZS',
  });
  const [promoUntil, setPromoUntil] = useState('');
  const [targetLanguages, setTargetLanguages] = useState<string[]>([]);
  // Бесплатный тариф — явное состояние, НЕ вывод из цен: пустой/null-набор цен схлопывался
  // в { base: 0 } и держал галочку включённой при создании и дублировании.
  const [isFreeTier, setIsFreeTier] = useState(false);
  const [cardAppearance, setCardAppearance] = useState<PricingTierCardAppearance | null>(null);
  const [appearanceTheme, setAppearanceTheme] = useState<'light' | 'dark'>('light');
  const [appearancePreviewOrientation, setAppearancePreviewOrientation] = useState<'horizontal' | 'vertical'>('horizontal');

  // Реинициализация state при открытии / смене тарифа
  useEffect(() => {
    if (!open) {
      return;
    }

    // В режиме создания при наличии prefill — используем его (дублирование),
    // но обнуляем prices и targetLanguages.
    const isDuplicate = !tier && !!prefill;
    const baseCurrency = source?.baseCurrency ?? 'UZS';
    const sourcePrices = isDuplicate
      ? { [baseCurrency]: 0 }
      : (source?.prices ?? null);
    const basePrice = isDuplicate ? 0 : (source?.prices?.[baseCurrency] ?? null);

    setDurationDays(source?.durationDays ? String(source.durationDays) : '');
    setPricing({
      price: basePrice,
      currency: baseCurrency,
      prices: sourcePrices,
      baseCurrency,
    });
    // Бесплатно = редактируем существующий тариф с непустым набором цен, и все они нули.
    // Дублирование и создание — всегда не бесплатно (админ вводит цену).
    const existingPrices = !isDuplicate ? source?.prices : null;
    setIsFreeTier(
      !!existingPrices &&
        Object.keys(existingPrices).length > 0 &&
        Object.values(existingPrices).every((v) => v === 0),
    );
    setTierIsActive(source?.isActive ?? true);
    setTierActiveLanguage('ru');
    setImageFile(source?.imageId && source?.imageUrl ? { id: source.imageId, url: source.imageUrl } : null);
    setGradientFrom(source?.gradientFrom ?? '');
    setGradientTo(source?.gradientTo ?? '');
    setGradientAngle(source?.gradientAngle !== null && source?.gradientAngle !== undefined ? String(source.gradientAngle) : '180');
    setTierIsFamily(source?.isFamily ?? false);
    setTierFamilySlots(String(source?.familySlots ?? 0));
    setPromoPricing({
      price: isDuplicate ? null : (source?.promoPrices?.[baseCurrency] ?? null),
      currency: baseCurrency,
      prices: isDuplicate ? null : (source?.promoPrices ?? null),
      baseCurrency,
    });
    setPromoUntil(!isDuplicate && source?.promoUntil ? source.promoUntil.slice(0, 16) : '');
    setTargetLanguages(isDuplicate ? [] : (source?.targetLanguages ?? []));
    setCardAppearance(isCardAppearance(source?.cardAppearance) ? source.cardAppearance : null);
    setAppearanceTheme('light');
    setAppearancePreviewOrientation('horizontal');

    const data = createEmptyTranslationData();
    if (source?.translations) {
      for (const [langCode, t] of Object.entries(source.translations)) {
        data[langCode as keyof TranslationData] = {
          name: t?.name ?? '',
        };
      }
    }
    setTierTranslations(data);
  }, [open, tier, prefill, source]);

  const saveMutation = useCustomMutation();
  const isSaving = saveMutation.mutation.isPending;

  // Чекбокс — UX-обёртка над ценами: бесплатно физически проставляет 0 по всем валютам.
  const handleToggleFree = (checked: boolean) => {
    setIsFreeTier(checked);
    if (checked) {
      const currentPrices = pricing.prices ?? { [pricing.baseCurrency]: pricing.price ?? 0 };
      const zeroed = Object.fromEntries(Object.keys(currentPrices).map((c) => [c, 0]));
      setPricing({ price: 0, currency: pricing.baseCurrency, prices: zeroed, baseCurrency: pricing.baseCurrency });
      // Промо на бесплатном тарифе не имеет смысла — сбрасываем
      setPromoPricing({ price: null, currency: pricing.baseCurrency, prices: null, baseCurrency: pricing.baseCurrency });
      setPromoUntil('');
    } else {
      // Возвращаем редактируемое поле, не угадывая сумму — оставляем 0, админ введёт цену
      setPricing({ price: 0, currency: pricing.baseCurrency, prices: { [pricing.baseCurrency]: 0 }, baseCurrency: pricing.baseCurrency });
    }
  };

  // Промо считается истёкшим, если дата в прошлом — сохранить можно, но акция неактивна
  const isPromoExpired = !!promoUntil && new Date(promoUntil).getTime() <= Date.now();

  const handleSaveTier = () => {
    const translationsArray: { languageCode: string; name: string }[] = [];
    for (const [code, data] of Object.entries(tierTranslations)) {
      if (data?.name) {
        translationsArray.push({ languageCode: code, name: data.name });
      }
    }

    // Валидация переводов для каждого выбранного targetLanguage
    if (targetLanguages.length > 0) {
      const missing: string[] = [];
      for (const code of targetLanguages) {
        const name = tierTranslations[code as keyof TranslationData]?.name?.trim();
        if (!name) {
          missing.push(code.toUpperCase());
        }
      }
      if (missing.length > 0) {
        notify.error(`Заполните название тарифа для языков: ${missing.join(', ')}`);
        return;
      }
    }

    const values = {
      ...productKey,
      durationDays: durationDays ? Number(durationDays) : null,
      prices: pricing.prices ?? { [pricing.baseCurrency]: pricing.price ?? 0 },
      baseCurrency: pricing.baseCurrency,
      promoPrices: promoPricing.prices && Object.keys(promoPricing.prices).length > 0 ? promoPricing.prices : null,
      promoUntil: promoUntil ? new Date(promoUntil).toISOString() : null,
      isActive: tierIsActive,
      imageId: imageFile?.id ?? null,
      gradientFrom: gradientFrom || null,
      gradientTo: gradientTo || null,
      gradientAngle: gradientAngle ? Number(gradientAngle) : 180,
      isFamily: tierIsFamily,
      familySlots: tierIsFamily ? (Number(tierFamilySlots) || 1) : 0,
      cardAppearance,
      translations: translationsArray,
      targetLanguages,
    };

    if (isEditTier) {
      let applyDecision = false;
      const doSave = () => new Promise<void>((resolve, reject) => {
        saveMutation.mutate(
          {
            url: `${API_URL}/pricing-tiers/admin/${tier.id}`,
            method: 'put',
            values: { ...values, applyToExisting: applyDecision },
          },
          {
            onSuccess: () => {
              notify.success(
                applyDecision
                  ? 'Тариф обновлён (применено к существующим подпискам)'
                  : 'Тариф обновлён',
              );
              onSaved();
              resolve();
            },
            onError: () => {
              notify.error('Ошибка при обновлении тарифа');
              reject();
            },
          },
        );
      });

      if (onCheckAffected) {
        const tierName = tierTranslations.ru?.name ?? `Тариф #${tier.id}`;
        onCheckAffected({
          changeDescription: `Обновление тарифа "${tierName}"`,
          operationType: 'update',
          onProceed: doSave,
          onApplyToExisting: () => {
            applyDecision = true;
            return Promise.resolve();
          },
        });
      } else {
        void doSave();
      }
    } else {
      saveMutation.mutate(
        {
          url: `${API_URL}/pricing-tiers/admin`,
          method: 'post',
          values,
        },
        {
          onSuccess: () => {
            notify.success('Тариф создан');
            onSaved();
          },
          onError: () => {
            notify.error('Ошибка при создании тарифа');
          },
        },
      );
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader className="pb-2">
          <div className="flex items-center justify-between pr-6">
            <SheetTitle>{isEditTier ? 'Редактировать тариф' : 'Новый тариф'}</SheetTitle>
            <div className="flex items-center gap-2">
              <Select
                value={tierIsActive ? 'active' : 'inactive'}
                onValueChange={(v) => setTierIsActive(v === 'active')}
              >
                <SelectTrigger className="h-8 w-[130px] text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Активен</SelectItem>
                  <SelectItem value="inactive">Неактивен</SelectItem>
                </SelectContent>
              </Select>
              {languages && languages.length > 0 && (
                <LanguageSwitcher
                  languages={languages}
                  activeLanguage={tierActiveLanguage}
                  onLanguageChange={(code) => setTierActiveLanguage(code as LanguageCode)}
                  fields={TIER_TRANSLATION_FIELDS}
                  value={tierTranslations}
                  variant="compact"
                />
              )}
            </div>
          </div>
        </SheetHeader>

        <div className="space-y-4">
          {languages && languages.length > 0 && (
            <TranslationsForm
              fields={TIER_TRANSLATION_FIELDS}
              value={tierTranslations}
              onChange={setTierTranslations}
              languages={languages}
              activeLanguage={tierActiveLanguage}
              onLanguageChange={(code) => setTierActiveLanguage(code as LanguageCode)}
            />
          )}

          <FormField
            label="Срок действия (дней)"
            name="tierDurationDays"
            hint="Оставьте пустым для бессрочного доступа"
          >
            <Input
              type="number"
              value={durationDays}
              onChange={(e) => setDurationDays(e.target.value)}
              placeholder="30"
            />
          </FormField>

          <FormField
            label="Видимость по языкам аудитории"
            name="targetLanguages"
            hint="Пустое = виден всем. При выборе языков обязательны переводы названия для каждого выбранного языка."
          >
            <MultiSelect
              options={(languages ?? []).map((l) => ({
                value: l.code,
                label: `${l.flag ?? ''} ${l.nativeName} (${l.code.toUpperCase()})`.trim(),
              }))}
              value={targetLanguages}
              onChange={setTargetLanguages}
              placeholder="Виден всем"
              searchPlaceholder="Поиск языка..."
              emptyMessage="Языки не найдены"
            />
          </FormField>

          {isEditTier && tier ? (
            <ContentTagsField
              entityType="PRICING_TIER"
              entityId={tier.id}
              label="Аудитория (теги)"
              description="Тариф виден только пользователям с пересечением тегов. Пусто = виден всем."
            />
          ) : null}

          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="tierIsFree"
                checked={isFreeTier}
                onCheckedChange={(checked) => handleToggleFree(checked === true)}
              />
              <label htmlFor="tierIsFree" className="text-sm font-medium leading-none">
                Бесплатный тариф
              </label>
            </div>
            {isFreeTier ? (
              <p className="text-xs text-muted-foreground">
                Доступ выдаётся бесплатно, без оплаты. Снимите галочку, чтобы указать цену.
              </p>
            ) : (
              <MultiCurrencyPrices
                value={pricing}
                onChange={setPricing}
                label="Ценообразование"
                required
              />
            )}
          </div>

          <Separator />

          {/* Промо-цена — недоступна для бесплатного тарифа */}
          {!isFreeTier && (
          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="promoEnabled"
                checked={!!promoUntil || (promoPricing.prices !== null && Object.keys(promoPricing.prices).length > 0)}
                onCheckedChange={(checked) => {
                  if (!checked) {
                    setPromoPricing({ price: null, currency: pricing.baseCurrency, prices: null, baseCurrency: pricing.baseCurrency });
                    setPromoUntil('');
                  } else {
                    const defaultUntil = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
                    defaultUntil.setSeconds(0);
                    defaultUntil.setMilliseconds(0);
                    setPromoUntil(defaultUntil.toISOString().slice(0, 16));
                  }
                }}
              />
              <label htmlFor="promoEnabled" className="text-sm font-medium leading-none">
                Промо-цена (временная акция)
              </label>
            </div>
            {(!!promoUntil || (promoPricing.prices !== null && Object.keys(promoPricing.prices).length > 0)) && (
              <>
                <MultiCurrencyPrices
                  value={promoPricing}
                  onChange={setPromoPricing}
                  label="Акционная цена"
                />
                <FormField label="Акция действует до" name="promoUntil">
                  <DateTimePicker
                    value={promoUntil ? new Date(promoUntil) : null}
                    onChange={(date) =>
                      setPromoUntil(date ? date.toISOString().slice(0, 16) : '')
                    }
                    placeholder="Не задано"
                  />
                </FormField>
                {isPromoExpired && (
                  <p className="text-xs text-amber-600">
                    Срок акции уже прошёл — промо-цена не применяется. Укажите дату в будущем, чтобы активировать.
                  </p>
                )}
              </>
            )}
          </div>
          )}

          <Separator />

          {productKey.subscriptionPlanId && (
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="tierIsFamily"
                  checked={tierIsFamily}
                  onCheckedChange={(checked) => setTierIsFamily(checked === true)}
                />
                <label htmlFor="tierIsFamily" className="text-sm font-medium leading-none flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Семейный тариф
                </label>
              </div>
              {tierIsFamily && (
                <FormField label="Количество мест в семье" name="tierFamilySlots">
                  <Input
                    type="number"
                    min="1"
                    value={tierFamilySlots}
                    onChange={(e) => setTierFamilySlots(e.target.value)}
                    placeholder="5"
                  />
                </FormField>
              )}
            </div>
          )}

          {/* Legacy «Оформление» (gradient + image) — только для не-абонементов
              (каналы/курсы/книги). У абонементов цвета карточки настраиваются
              в секции «Карточка абонемента» ниже (cardAppearance). */}
          {!productKey.subscriptionPlanId && (
          <>
          <Separator />

          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Palette className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Оформление</span>
              <span className="text-xs text-muted-foreground">(опционально)</span>
            </div>

            <div className="flex flex-wrap gap-1.5">
              {GRADIENT_PRESETS.map((preset) => (
                <button
                  key={preset.name}
                  type="button"
                  className="flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs hover:bg-accent transition-colors"
                  onClick={() => {
                    setGradientFrom(preset.from);
                    setGradientTo(preset.to);
                    setGradientAngle(String(preset.angle));
                  }}
                >
                  <div
                    className="h-3 w-3 rounded-full shrink-0 border"
                    style={{
                      background: `linear-gradient(${preset.angle}deg, ${preset.from}, ${preset.to})`,
                    }}
                  />
                  {preset.name}
                </button>
              ))}
              {(gradientFrom || gradientTo) && (
                <button
                  type="button"
                  className="rounded-md border px-2 py-1 text-xs text-muted-foreground hover:bg-accent transition-colors"
                  onClick={() => {
                    setGradientFrom('');
                    setGradientTo('');
                    setGradientAngle('180');
                  }}
                >
                  Сбросить
                </button>
              )}
            </div>

            <div className="grid grid-cols-3 gap-3">
              <FormField label="Цвет от" name="gradientFrom">
                <ColorPicker
                  value={gradientFrom}
                  onChange={setGradientFrom}
                  fallbackColor="#FCA311"
                  placeholder="#FCA311"
                />
              </FormField>

              <FormField label="Цвет до" name="gradientTo">
                <ColorPicker
                  value={gradientTo}
                  onChange={setGradientTo}
                  fallbackColor="#FFD99A"
                  placeholder="#FFD99A"
                />
              </FormField>

              <FormField label="Угол" name="gradientAngle">
                <Input
                  type="number"
                  value={gradientAngle}
                  onChange={(e) => setGradientAngle(e.target.value)}
                  min={0}
                  max={360}
                  placeholder="180"
                />
              </FormField>
            </div>

            {gradientFrom && gradientTo && (
              <div
                className="h-10 w-full rounded-md border"
                style={{
                  background: `linear-gradient(${gradientAngle || 180}deg, ${gradientFrom}, ${gradientTo})`,
                }}
              />
            )}
          </div>
          </>
          )}

          {productKey.subscriptionPlanId && (
            <>
              <Separator />
              <CardAppearanceSection
                value={cardAppearance}
                onChange={setCardAppearance}
                theme={appearanceTheme}
                onThemeChange={setAppearanceTheme}
                previewOrientation={appearancePreviewOrientation}
                onPreviewOrientationChange={setAppearancePreviewOrientation}
                tierIsFamily={tierIsFamily}
                tierName={tierTranslations.ru?.name ?? null}
                durationDays={durationDays ? Number(durationDays) : null}
              />
            </>
          )}

        </div>

        <SheetFooter className="flex-row items-center justify-end gap-2 p-4">
          <Button variant="outline" size="default" onClick={() => onOpenChange(false)}>
            Отмена
          </Button>
          <Button size="default" onClick={handleSaveTier} disabled={isSaving || (!isFreeTier && !pricing.price)}>
            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isEditTier ? 'Сохранить' : 'Создать'}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

/* ─── Компонент с табами "Доступы" / "Кастомные" ───────────────── */

interface BonusTabsSectionProps {
  bonuses: BonusTemplate[];
  tier: PricingTier;
  onAddAccess: () => void;
  onEditAccess: (bonus: BonusTemplate) => void;
  onDeleteBonus: (bonus: BonusTemplate) => void;
  onAddBonus: () => void;
  onEditBonus: (bonus: BonusTemplate) => void;
  onReorderBonuses: (reordered: BonusTemplate[]) => void;
}

function BonusTabsSection({
  bonuses,
  tier,
  onAddAccess,
  onEditAccess,
  onDeleteBonus,
  onAddBonus,
  onEditBonus,
  onReorderBonuses,
}: BonusTabsSectionProps) {
  // Access-доступы теперь живут в tier.entitlements (новая модель).
  // Конвертируем в BonusTemplate-shape для обратной совместимости UI.
  const accessBonuses = (tier.entitlements ?? []).map(entitlementToBonusTemplate);
  // Custom bonuses — настоящие привилегии (без access-семантики)
  const customBonuses = bonuses.filter((b) => !isAccessBonus(b));

  return (
    <div>
      <p className="text-sm font-semibold mb-2">Привилегии</p>
        <Tabs defaultValue="access">
          <TabsList className="w-full">
            <TabsTrigger value="access" className="flex-1">
              <Lock className="mr-1.5 h-3.5 w-3.5" />
              Доступы
              {accessBonuses.length > 0 && (
                <Badge variant="secondary" className="ml-1.5 h-5 px-1.5 text-xs">
                  {accessBonuses.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="custom" className="flex-1">
              <Tag className="mr-1.5 h-3.5 w-3.5" />
              Кастомные
              {customBonuses.length > 0 && (
                <Badge variant="secondary" className="ml-1.5 h-5 px-1.5 text-xs">
                  {customBonuses.length}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="access" forceMount className="data-[state=inactive]:hidden">
            <div className="space-y-2">
              <div className="flex justify-end">
                <Button size="sm" variant="outline" onClick={onAddAccess}>
                  <Plus className="mr-1 h-3 w-3" />
                  Добавить доступ
                </Button>
              </div>
              {accessBonuses.length === 0 ? (
                <div className="text-muted-foreground py-4 text-center text-xs">
                  Нет доступов. Добавьте доступ к курсу, каналу или событию.
                </div>
              ) : (
                <div className="space-y-1.5">
                  {accessBonuses.map((bonus) => {
                    const accessVal = parseAccessValue(bonus);
                    const isQty = accessVal ? isQuantityAccess(accessVal) : false;
                    const Icon = isQty ? Hash : (accessVal ? getAccessIcon(accessVal.accessTargetType) : Lock);
                    const label = accessVal ? getAccessLabel(accessVal.accessTargetType) : 'Доступ';
                    const targetName = isQty && accessVal
                      ? `${accessVal.quantity ?? 0} ${label.toLowerCase()}${(accessVal.quantity ?? 0) === 1 ? '' : (label === 'Курс' ? 'а' : 'ов')} по выбору`
                      : (accessVal?.targetName ?? 'Неизвестно');

                    return (
                      <div
                        key={bonus.id}
                        className="flex items-center gap-2 rounded border p-2 text-sm"
                      >
                        <div className="flex flex-1 items-center gap-2 min-w-0">
                          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded bg-muted">
                            <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="truncate font-medium text-xs">{targetName}</div>
                            <div className="text-muted-foreground text-[11px]">
                              {isQty ? `${label} · количество по выбору` : label}
                              {bonus.expiresAt && (
                                <span className="ml-1">
                                  · до {format(new Date(bonus.expiresAt), 'dd.MM.yyyy', { locale: ru })}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => onEditAccess(bonus)}
                          >
                            <Pencil className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive h-7 w-7"
                            onClick={() => onDeleteBonus(bonus)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="custom" forceMount className="data-[state=inactive]:hidden">
            <div className="space-y-2">
              <div className="flex justify-end">
                <Button size="sm" variant="outline" onClick={onAddBonus}>
                  <Plus className="mr-1 h-3 w-3" />
                  Добавить
                </Button>
              </div>
              {customBonuses.length === 0 ? (
                <div className="text-muted-foreground py-4 text-center text-xs">
                  Нет кастомных привилегий
                </div>
              ) : (
                <SortableList
                  items={customBonuses}
                  onReorder={(reordered) => {
                    // Reorder только кастомных bonuses; access живёт в entitlements
                    // и переупорядочивается через отдельный endpoint.
                    onReorderBonuses(reordered as BonusTemplate[]);
                  }}
                  renderItem={({ item, dragHandleProps }) => {
                    const b = item as BonusTemplate;
                    const bonusName =
                      b.translations?.ru?.name ??
                      b.translations?.en?.name ??
                      `Привилегия #${b.id}`;
                    return (
                      <div className="flex items-center gap-2 rounded border p-2 text-sm">
                        <div {...dragHandleProps}>
                          <DragHandle />
                        </div>
                        <div className="flex flex-1 items-center gap-2 min-w-0">
                          <Badge variant="outline" className="text-xs shrink-0">
                            {BONUS_TYPES.find((bt) => bt.value === b.type)?.label ?? b.type}
                          </Badge>
                          <span className="truncate">{bonusName}</span>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => onEditBonus(b)}
                          >
                            <Pencil className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive h-7 w-7"
                            onClick={() => onDeleteBonus(b)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    );
                  }}
                />
              )}
            </div>
          </TabsContent>
        </Tabs>
    </div>
  );
}

/* ─── AccessBonusModal ─────────────────────────────────────────── */

interface AccessBonusSaveData {
  accessTargetType: AccessTargetTypeValue;
  selectionMode: AccessSelectionMode;
  targetId: number;
  targetName: string;
  quantity: number | null;
  courseType: string | null;
  durationMode: BonusTemplate['durationMode'];
  customDurationDays: number | null;
  expiresAt: Date | null;
}

interface AccessBonusModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  bonus: BonusTemplate | null;
  onSave: (data: AccessBonusSaveData) => void;
  isSaving: boolean;
}

function AccessBonusModal({ open, onOpenChange, bonus, onSave, isSaving }: AccessBonusModalProps) {
  const existingAccess = bonus ? parseAccessValue(bonus) : null;
  const isEdit = !!bonus;

  const [targetType, setTargetType] = useState<AccessTargetTypeValue>(
    existingAccess?.accessTargetType ?? 'COURSE',
  );
  const [selectionMode, setSelectionMode] = useState<AccessSelectionMode>(
    existingAccess ? getSelectionMode(existingAccess) : 'specific',
  );
  const [targetId, setTargetId] = useState<number | null>(existingAccess?.targetId ?? null);
  const [targetName, setTargetName] = useState(existingAccess?.targetName ?? '');
  const [quantity, setQuantity] = useState(
    existingAccess?.quantity ? String(existingAccess.quantity) : '',
  );
  const [courseTypeFilter, setCourseTypeFilter] = useState(
    existingAccess?.courseType ?? '',
  );
  const [durationMode, setDurationMode] = useState<BonusTemplate['durationMode']>(
    bonus ? apiToUiDurationMode(bonus.durationMode) : 'TIER',
  );
  const [customDurationDays, setCustomDurationDays] = useState(
    bonus?.customDurationDays ? String(bonus.customDurationDays) : '',
  );
  const [expiresAt, setExpiresAt] = useState<Date | null>(
    bonus?.expiresAt ? new Date(bonus.expiresAt) : null,
  );

  // Сброс при открытии/смене бонуса
  useEffect(() => {
    if (!open) {
      return;
    }
    const access = bonus ? parseAccessValue(bonus) : null;
    setTargetType(access?.accessTargetType ?? 'COURSE');
    setSelectionMode(access ? getSelectionMode(access) : 'specific');
    setTargetId(access?.targetId ?? null);
    setTargetName(access?.targetName ?? '');
    setQuantity(access?.quantity ? String(access.quantity) : '');
    setCourseTypeFilter(access?.courseType ?? '');
    setDurationMode(bonus ? apiToUiDurationMode(bonus.durationMode) : 'TIER');
    setCustomDurationDays(bonus?.customDurationDays ? String(bonus.customDurationDays) : '');
    setExpiresAt(bonus?.expiresAt ? new Date(bonus.expiresAt) : null);
  }, [open, bonus]);

  // Загружаем курсы и каналы (лёгкие эндпоинты)
  const apiUrl = useApiUrl();
  const coursesUrl = courseTypeFilter
    ? `${apiUrl}/courses/options?courseType=${courseTypeFilter}`
    : `${apiUrl}/courses/options`;
  const { query: coursesQuery } = useCustom<{ id: number; title: string; courseType: string }[]>({
    url: coursesUrl,
    method: 'get',
    queryOptions: { enabled: open },
  });
  const { query: channelsQuery } = useCustom<{ id: number; title: string; username: string | null }[]>({
    url: `${apiUrl}/telegram-channels/options`,
    method: 'get',
    queryOptions: { enabled: open },
  });
  const { query: booksQuery } = useCustom<{ id: number; title: string }[]>({
    url: `${apiUrl}/books/options`,
    method: 'get',
    queryOptions: { enabled: open && targetType === 'BOOK' },
  });

  const courseOptions = coursesQuery.data?.data ?? [];
  const channelOptions = channelsQuery.data?.data ?? [];
  const bookOptions = booksQuery.data?.data ?? [];
  const isLoadingEntities = coursesQuery.isLoading || channelsQuery.isLoading || booksQuery.isLoading;

  const getTargetOptions = (): { value: string; label: string }[] => {
    if (targetType === 'COURSE') {
      return courseOptions.map((c) => ({ value: String(c.id), label: c.title }));
    }
    if (targetType === 'CHANNEL') {
      return channelOptions.map((c) => ({
        value: String(c.id),
        label: c.username ? `${c.title} (@${c.username})` : c.title,
      }));
    }
    if (targetType === 'BOOK') {
      return bookOptions.map((b) => ({ value: String(b.id), label: b.title }));
    }
    return [];
  };

  const targetTypeLabel = ACCESS_TARGET_TYPES.find((t) => t.value === targetType)?.label ?? targetType;
  const targetOptions = getTargetOptions();

  const handleTargetChange = (val: string) => {
    if (val === 'all') {
      setTargetId(null);
      setTargetName(`Все ${targetTypeLabel.toLowerCase()}ы`);
    } else {
      const numId = Number(val);
      setTargetId(numId);
      const opt = targetOptions.find((o) => o.value === val);
      setTargetName(opt?.label ?? `#${numId}`);
    }
  };

  const handleSelectionModeChange = (mode: AccessSelectionMode) => {
    setSelectionMode(mode);
    if (mode === 'quantity') {
      setTargetId(null);
      setTargetName('');
    }
  };

  const canSaveExpires = durationMode !== 'ABSOLUTE' || expiresAt !== null;
  const canSaveQuantity = selectionMode !== 'quantity' || (quantity && Number(quantity) > 0);

  const handleSave = () => {
    if (!canSaveExpires || !canSaveQuantity) {
      return;
    }

    const quantityNum = selectionMode === 'quantity' ? Number(quantity) : null;
    const courseTypeLabel = COURSE_TYPES.find((ct) => ct.value === courseTypeFilter)?.label ?? '';
    const courseTypeSuffix = courseTypeFilter && targetType === 'COURSE'
      ? ` (${courseTypeLabel.toLowerCase()})`
      : '';
    const resolvedTargetName = selectionMode === 'quantity'
      ? `${quantityNum} ${targetTypeLabel.toLowerCase()}${quantityNum === 1 ? '' : (targetTypeLabel === 'Курс' ? 'а' : 'ов')} по выбору${courseTypeSuffix}`
      : (targetName || `Все ${targetTypeLabel.toLowerCase()}ы`);

    onSave({
      accessTargetType: targetType,
      selectionMode,
      targetId: selectionMode === 'quantity' ? 0 : (targetId ?? 0),
      targetName: resolvedTargetName,
      quantity: quantityNum,
      courseType: selectionMode === 'quantity' && targetType === 'COURSE' && courseTypeFilter
        ? courseTypeFilter
        : null,
      durationMode,
      customDurationDays: durationMode === 'CUSTOM' && customDurationDays ? Number(customDurationDays) : null,
      expiresAt: durationMode === 'ABSOLUTE' ? expiresAt : null,
    });
  };

  const selectedDurationHint = DURATION_MODES.find((dm) => dm.value === durationMode)?.hint;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader className="pb-2">
          <SheetTitle>{isEdit ? 'Редактировать доступ' : 'Новый доступ'}</SheetTitle>
        </SheetHeader>

        <div className="space-y-4">
          <FormField label="Тип" name="accessTargetType" required>
            <Select
              value={targetType}
              onValueChange={(v) => {
                setTargetType(v as AccessTargetTypeValue);
                setTargetId(null);
                setTargetName('');
              }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ACCESS_TARGET_TYPES.map((at) => {
                  const Icon = at.icon;
                  return (
                    <SelectItem key={at.value} value={at.value}>
                      <div className="flex items-center gap-2">
                        <Icon className="h-4 w-4" />
                        {at.label}
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </FormField>

          <FormField label="Режим выбора" name="selectionMode">
            <RadioGroup
              value={selectionMode}
              onValueChange={(v) => handleSelectionModeChange(v as AccessSelectionMode)}
              className="flex gap-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="specific" id="mode-specific" />
                <Label htmlFor="mode-specific" className="text-sm font-normal cursor-pointer">
                  Конкретный объект
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="quantity" id="mode-quantity" />
                <Label htmlFor="mode-quantity" className="text-sm font-normal cursor-pointer">
                  Количество по выбору
                </Label>
              </div>
            </RadioGroup>
          </FormField>

          {selectionMode === 'specific' ? (
            <FormField label={targetTypeLabel} name="accessTarget">
              {isLoadingEntities ? (
                <div className="flex items-center gap-2 py-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Загрузка...
                </div>
              ) : (
                <Select
                  value={targetId !== null && targetId !== undefined ? String(targetId) : 'all'}
                  onValueChange={handleTargetChange}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Выберите..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">
                      Все {targetTypeLabel.toLowerCase()}ы
                    </SelectItem>
                    {targetOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </FormField>
          ) : (
            <>
              <FormField
                label={`Количество ${targetTypeLabel === 'Курс' ? 'курсов' : 'каналов'}`}
                name="accessQuantity"
                required
                hint="Пользователь сможет выбрать указанное количество объектов бесплатно"
              >
                <Input
                  type="number"
                  min="1"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  placeholder="3"
                />
              </FormField>

              {targetType === 'COURSE' && (
                <FormField
                  label="Тип курсов"
                  name="courseTypeFilter"
                  hint="Ограничить бонус определённым типом курсов"
                >
                  <Select
                    value={courseTypeFilter || '__all__'}
                    onValueChange={(v) => setCourseTypeFilter(v === '__all__' ? '' : v)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Все типы" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">Все типы</SelectItem>
                      <SelectItem value="ONLINE">Онлайн</SelectItem>
                      <SelectItem value="MEETING">Клуб</SelectItem>
                    </SelectContent>
                  </Select>
                </FormField>
              )}
            </>
          )}

          <Separator />

          <FormField
            label="Режим срока действия"
            name="accessDurationMode"
            required
            hint={selectedDurationHint}
          >
            <Select
              value={durationMode}
              onValueChange={(v) => setDurationMode(v as BonusTemplate['durationMode'])}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DURATION_MODES.map((dm) => (
                  <SelectItem key={dm.value} value={dm.value}>
                    {dm.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>

          {durationMode === 'CUSTOM' && (
            <FormField label="Свой срок (дней)" name="accessCustomDuration" required>
              <Input
                type="number"
                value={customDurationDays}
                onChange={(e) => setCustomDurationDays(e.target.value)}
                placeholder="30"
              />
            </FormField>
          )}

          {durationMode === 'ABSOLUTE' && (
            <FormField label="Действует до" name="accessExpiresAt" required>
              <DateTimePicker
                value={expiresAt}
                onChange={setExpiresAt}
                placeholder="Выберите дату окончания"
                minDate={new Date()}
              />
            </FormField>
          )}
        </div>

        <SheetFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Отмена
          </Button>
          <Button onClick={handleSave} disabled={isSaving || !canSaveExpires || !canSaveQuantity}>
            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isEdit ? 'Сохранить' : 'Добавить'}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

interface BonusModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  bonus: BonusTemplate | null;
  onSave: (data: {
    type: BonusTemplate['type'];
    initialValue: string;
    durationMode: BonusTemplate['durationMode'];
    customDurationDays: number | null;
    order: number;
    translations: Record<string, { name: string; description?: string }>;
  }) => void;
  isSaving: boolean;
  languages?: TranslationLanguage[];
}

function BonusModal({ open, onOpenChange, bonus, onSave, isSaving, languages }: BonusModalProps) {
  const isEditBonus = !!bonus;

  const [type, setType] = useState<BonusTemplate['type']>(bonus?.type ?? 'NUMBER');
  const [initialValue, setInitialValue] = useState(() => {
    if (!bonus) {
      return '';
    }
    if (typeof bonus.initialValue === 'string') {
      return bonus.initialValue;
    }
    return JSON.stringify(bonus.initialValue);
  });
  const [durationMode, setDurationMode] = useState<BonusTemplate['durationMode']>(
    bonus ? apiToUiDurationMode(bonus.durationMode) : 'TIER',
  );
  const [customDurationDays, setCustomDurationDays] = useState(
    bonus?.customDurationDays ? String(bonus.customDurationDays) : '',
  );
  const [bonusActiveLanguage, setBonusActiveLanguage] = useState<LanguageCode>('ru');
  const [bonusTranslations, setBonusTranslations] = useState<TranslationData>(() => {
    const data = createEmptyTranslationData();
    if (bonus?.translations) {
      for (const [langCode, t] of Object.entries(bonus.translations)) {
        data[langCode as keyof TranslationData] = {
          name: t?.name ?? '',
          description: t?.description ?? '',
        };
      }
    }
    return data;
  });

  const selectedDurationHint = DURATION_MODES.find((dm) => dm.value === durationMode)?.hint;

  const hasRequiredName = !!(bonusTranslations.ru?.name?.trim());

  const handleSave = () => {
    const translations: Record<string, { name: string; description?: string }> = {};

    for (const [langCode, langData] of Object.entries(bonusTranslations)) {
      if (langData?.name?.trim()) {
        translations[langCode] = {
          name: langData.name,
          description: langData.description?.trim() || undefined,
        };
      }
    }

    onSave({
      type,
      initialValue,
      durationMode,
      customDurationDays:
        durationMode === 'CUSTOM' && customDurationDays ? Number(customDurationDays) : null,
      order: bonus?.order ?? 0,
      translations,
    });
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader className="pb-2">
          <div className="flex items-center justify-between pr-6">
            <SheetTitle>{isEditBonus ? 'Редактировать привилегию' : 'Новая привилегия'}</SheetTitle>
            {languages && languages.length > 0 && (
              <LanguageSwitcher
                languages={languages}
                activeLanguage={bonusActiveLanguage}
                onLanguageChange={(code) => setBonusActiveLanguage(code as LanguageCode)}
                fields={BONUS_TRANSLATION_FIELDS}
                value={bonusTranslations}
                variant="compact"
              />
            )}
          </div>
        </SheetHeader>

        <div className="space-y-4">
          {languages && languages.length > 0 && (
            <TranslationsForm
              fields={BONUS_TRANSLATION_FIELDS}
              value={bonusTranslations}
              onChange={setBonusTranslations}
              languages={languages}
              activeLanguage={bonusActiveLanguage}
              onLanguageChange={(code) => setBonusActiveLanguage(code as LanguageCode)}
            />
          )}

          <FormField
            label="Тип значения"
            name="bonusType"
            required
          >
            <Select value={type} onValueChange={(v) => setType(v as BonusTemplate['type'])}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {BONUS_TYPES.map((bt) => (
                  <SelectItem key={bt.value} value={bt.value}>
                    {bt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>

          <FormField
            label="Начальное значение"
            name="bonusInitialValue"
            hint={
              (() => {
                if (type === 'BOOLEAN') { return 'true или false'; }
                if (type === 'ARRAY') { return 'JSON массив, напр. []'; }
                if (type === 'NUMBER') { return 'Числовое значение, напр. 0'; }
                return;
              })()
            }
          >
            <Input
              value={initialValue}
              onChange={(e) => setInitialValue(e.target.value)}
              placeholder={
                (() => {
                  if (type === 'NUMBER') { return '0'; }
                  if (type === 'BOOLEAN') { return 'true'; }
                  if (type === 'ARRAY') { return '[]'; }
                  return '';
                })()
              }
            />
          </FormField>

          <FormField
            label="Режим срока действия"
            name="bonusDurationMode"
            required
            hint={selectedDurationHint}
          >
            <Select
              value={durationMode}
              onValueChange={(v) => setDurationMode(v as BonusTemplate['durationMode'])}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DURATION_MODES.map((dm) => (
                  <SelectItem key={dm.value} value={dm.value}>
                    {dm.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>

          {durationMode === 'CUSTOM' && (
            <FormField label="Свой срок (дней)" name="bonusCustomDuration" required>
              <Input
                type="number"
                value={customDurationDays}
                onChange={(e) => setCustomDurationDays(e.target.value)}
                placeholder="30"
              />
            </FormField>
          )}

        </div>

        <SheetFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Отмена
          </Button>
          <Button onClick={handleSave} disabled={isSaving || !hasRequiredName}>
            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isEditBonus ? 'Сохранить' : 'Создать'}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

interface CardAppearanceSectionProps {
  value: PricingTierCardAppearance | null;
  onChange: (value: PricingTierCardAppearance | null) => void;
  theme: 'light' | 'dark';
  onThemeChange: (theme: 'light' | 'dark') => void;
  previewOrientation: 'horizontal' | 'vertical';
  onPreviewOrientationChange: (orientation: 'horizontal' | 'vertical') => void;
  tierIsFamily: boolean;
  tierName: string | null;
  durationDays: number | null;
}

/**
 * Секция «Карточка абонемента» в TierModal (online editor).
 * Идентична по поведению секции в PricingTierDialog (offline editor wizard).
 */
function CardAppearanceSection({
  value,
  onChange,
  theme,
  onThemeChange,
  previewOrientation,
  onPreviewOrientationChange,
  tierIsFamily,
  tierName,
  durationDays,
}: CardAppearanceSectionProps) {
  const effective: PricingTierCardAppearance = value ?? DEFAULT_TIER_CARD_APPEARANCE;

  const updateColor = (
    targetTheme: 'light' | 'dark',
    key: keyof PricingTierCardThemeColors,
    color: string,
  ) => {
    const base = value ?? DEFAULT_TIER_CARD_APPEARANCE;
    const next: PricingTierCardAppearance = {
      light: { ...base.light },
      dark: { ...base.dark },
    };
    next[targetTheme] = { ...next[targetTheme], [key]: color };
    onChange(next);
  };

  const previewTierName = tierName?.trim() || 'Разовый абонемент';
  const previewExpiresAt = useMemo(() => {
    if (!durationDays) { return null; }
    const date = new Date();
    date.setDate(date.getDate() + durationDays);
    return date.toISOString();
  }, [durationDays]);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <CreditCard className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">Карточка абонемента</span>
          <span className="text-xs text-muted-foreground">(опционально)</span>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onChange(null)}
          disabled={value === null}
        >
          Сбросить к дефолтам
        </Button>
      </div>

      <Tabs
        value={theme}
        onValueChange={(v) => {
          if (v === 'light' || v === 'dark') { onThemeChange(v); }
        }}
      >
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="light">Светлая тема</TabsTrigger>
          <TabsTrigger value="dark">Тёмная тема</TabsTrigger>
        </TabsList>

        <TabsContent value="light" className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <FormField label="Фон" name="appearanceLightBg">
              <ColorPicker
                value={effective.light.bg}
                onChange={(color) => updateColor('light', 'bg', color)}
                fallbackColor={DEFAULT_TIER_CARD_APPEARANCE.light.bg}
                placeholder={DEFAULT_TIER_CARD_APPEARANCE.light.bg}
              />
            </FormField>
            <FormField label="Паттерн (основной)" name="appearanceLightPP">
              <ColorPicker
                value={effective.light.patternPrimary}
                onChange={(color) => updateColor('light', 'patternPrimary', color)}
                fallbackColor={DEFAULT_TIER_CARD_APPEARANCE.light.patternPrimary}
                placeholder={DEFAULT_TIER_CARD_APPEARANCE.light.patternPrimary}
              />
            </FormField>
            <FormField label="Паттерн (вторичный)" name="appearanceLightPS">
              <ColorPicker
                value={effective.light.patternSecondary}
                onChange={(color) => updateColor('light', 'patternSecondary', color)}
                fallbackColor={DEFAULT_TIER_CARD_APPEARANCE.light.patternSecondary}
                placeholder={DEFAULT_TIER_CARD_APPEARANCE.light.patternSecondary}
              />
            </FormField>
          </div>
        </TabsContent>

        <TabsContent value="dark" className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <FormField label="Фон" name="appearanceDarkBg">
              <ColorPicker
                value={effective.dark.bg}
                onChange={(color) => updateColor('dark', 'bg', color)}
                fallbackColor={DEFAULT_TIER_CARD_APPEARANCE.dark.bg}
                placeholder={DEFAULT_TIER_CARD_APPEARANCE.dark.bg}
              />
            </FormField>
            <FormField label="Паттерн (основной)" name="appearanceDarkPP">
              <ColorPicker
                value={effective.dark.patternPrimary}
                onChange={(color) => updateColor('dark', 'patternPrimary', color)}
                fallbackColor={DEFAULT_TIER_CARD_APPEARANCE.dark.patternPrimary}
                placeholder={DEFAULT_TIER_CARD_APPEARANCE.dark.patternPrimary}
              />
            </FormField>
            <FormField label="Паттерн (вторичный)" name="appearanceDarkPS">
              <ColorPicker
                value={effective.dark.patternSecondary}
                onChange={(color) => updateColor('dark', 'patternSecondary', color)}
                fallbackColor={DEFAULT_TIER_CARD_APPEARANCE.dark.patternSecondary}
                placeholder={DEFAULT_TIER_CARD_APPEARANCE.dark.patternSecondary}
              />
            </FormField>
          </div>
        </TabsContent>
      </Tabs>

      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">Превью</span>
        <div className="flex gap-1">
          <Button
            type="button"
            variant={previewOrientation === 'horizontal' ? 'default' : 'outline'}
            size="sm"
            onClick={() => onPreviewOrientationChange('horizontal')}
          >
            Горизонтальная
          </Button>
          <Button
            type="button"
            variant={previewOrientation === 'vertical' ? 'default' : 'outline'}
            size="sm"
            onClick={() => onPreviewOrientationChange('vertical')}
          >
            Вертикальная
          </Button>
        </div>
      </div>

      <div className="flex justify-center overflow-x-auto rounded-md border bg-muted/30 p-4">
        <MembershipCardPreview
          appearance={value}
          theme={theme}
          tierName={previewTierName}
          userName="DALER MUSTAFAEV"
          expiresAt={previewExpiresAt}
          isFamily={tierIsFamily}
          labels={{
            validUntil: 'Действует до',
            validity: 'Срок действия:',
            lifetime: 'Бессрочно',
            family: 'Семейный',
          }}
          orientation={previewOrientation}
        />
      </div>
    </div>
  );
}
